import React, { useState } from 'react';
import { ScienceDebugger, DiagnosticStep, CheckState } from './ScienceDebugger';
import { RoutingScience } from './RoutingScience';
import { StorageScience } from './StorageScience';
import { DeployScience } from './DeployScience';
import { ErrorScience } from './ErrorScience';

interface SystemHealth {
  name: string;
  passed: number;
  failed: number;
  total: number;
  status: 'healthy' | 'warning' | 'critical';
}

export default function ScienceDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [systemHealth, setSystemHealth] = useState<SystemHealth[]>([]);
  const [isRunningAll, setIsRunningAll] = useState(false);

  // Performance diagnostics
  const performanceSteps: DiagnosticStep[] = [
    {
      id: 'page-load-time',
      label: 'Page load time acceptable',
      category: 'Performance',
      check: () => {
        const perf = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const loadTime = perf ? perf.loadEventEnd - perf.fetchStart : 0;
        return {
          passed: loadTime < 3000,
          details: {
            loadTimeMs: loadTime.toFixed(0),
            domContentLoadedMs: perf ? (perf.domContentLoadedEventEnd - perf.fetchStart).toFixed(0) : 0,
            firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime.toFixed(0),
          }
        };
      }
    },
    {
      id: 'memory-usage',
      label: 'Memory usage within limits',
      category: 'Performance',
      check: () => {
        const memory = (performance as any).memory;
        if (memory) {
          const usedPercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
          return {
            passed: usedPercent < 90,
            details: {
              usedMB: (memory.usedJSHeapSize / 1024 / 1024).toFixed(2),
              totalMB: (memory.totalJSHeapSize / 1024 / 1024).toFixed(2),
              limitMB: (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2),
              usedPercent: usedPercent.toFixed(2) + '%',
            }
          };
        }
        return {
          passed: true,
          details: { note: 'Memory API not available' }
        };
      }
    },
    {
      id: 'dom-size',
      label: 'DOM size is reasonable',
      category: 'Performance',
      check: () => {
        const nodeCount = document.getElementsByTagName('*').length;
        return {
          passed: nodeCount < 1500,
          details: {
            nodeCount,
            warning: nodeCount > 1000 ? 'Consider optimizing DOM size' : 'DOM size is optimal',
          }
        };
      }
    },
  ];

  // Network diagnostics
  const networkSteps: DiagnosticStep[] = [
    {
      id: 'online-status',
      label: 'Network connection active',
      category: 'Network',
      critical: true,
      check: () => ({
        passed: navigator.onLine,
        details: {
          online: navigator.onLine,
          effectiveType: (navigator as any).connection?.effectiveType,
          downlink: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
        }
      })
    },
    {
      id: 'api-health',
      label: 'API endpoint reachable',
      category: 'Network',
      check: async () => {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
        const startTime = performance.now();
        try {
          const response = await fetch(`${apiUrl}/health`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000),
          });
          const responseTime = performance.now() - startTime;
          return {
            passed: response.ok,
            details: {
              status: response.status,
              responseTimeMs: responseTime.toFixed(0),
              apiUrl,
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: 'API unreachable',
            details: {
              responseTimeMs: (performance.now() - startTime).toFixed(0),
            }
          };
        }
      }
    },
    {
      id: 'websocket-support',
      label: 'WebSocket support available',
      category: 'Network',
      check: () => ({
        passed: 'WebSocket' in window,
        details: {
          supported: 'WebSocket' in window,
          secure: window.location.protocol === 'https:',
        }
      })
    },
  ];

  // Browser diagnostics
  const browserSteps: DiagnosticStep[] = [
    {
      id: 'browser-version',
      label: 'Browser version detection',
      category: 'Browser',
      check: () => ({
        passed: true,
        details: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          cookiesEnabled: navigator.cookieEnabled,
        }
      })
    },
    {
      id: 'viewport-size',
      label: 'Viewport dimensions',
      category: 'Browser',
      check: () => ({
        passed: true,
        details: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
        }
      })
    },
    {
      id: 'required-apis',
      label: 'Required browser APIs available',
      category: 'Browser',
      check: () => {
        const requiredAPIs = {
          'Fetch API': 'fetch' in window,
          'Promise': 'Promise' in window,
          'LocalStorage': 'localStorage' in window,
          'JSON': 'JSON' in window,
          'Crypto': 'crypto' in window,
          'URL API': 'URL' in window,
        };

        const allAvailable = Object.values(requiredAPIs).every(v => v);

        return {
          passed: allAvailable,
          details: requiredAPIs,
        };
      }
    },
  ];

  const handleSystemComplete = (name: string, results: CheckState[]) => {
    const passed = results.filter(r => r.status === 'passed').length;
    const failed = results.filter(r => r.status === 'failed').length;
    const total = results.length;

    let status: SystemHealth['status'] = 'healthy';
    if (failed > 0) {
      status = failed > total / 2 ? 'critical' : 'warning';
    }

    setSystemHealth(prev => {
      const updated = prev.filter(s => s.name !== name);
      return [...updated, { name, passed, failed, total, status }];
    });
  };

  const runAllDiagnostics = async () => {
    setIsRunningAll(true);
    setSystemHealth([]);
    // The individual components will handle their own running
    // We just need to trigger them somehow - this would require refs or state management
    setIsRunningAll(false);
  };

  const tabs = [
    { id: 'overview', label: '🏠 Overview', icon: '📊' },
    { id: 'errors', label: '🔬 Errors', icon: '🔬' },
    { id: 'deployment', label: '🚀 Deployment', icon: '🚀' },
    { id: 'performance', label: '⚡ Performance', icon: '⚡' },
    { id: 'network', label: '🌐 Network', icon: '🌐' },
    { id: 'browser', label: '🖥️ Browser', icon: '🖥️' },
    { id: 'routing', label: '🧭 Routing', icon: '🧭' },
    { id: 'storage', label: '💾 Storage', icon: '💾' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                🔬 Science Dashboard
              </h1>
              <p className="text-lg text-gray-600">
                Transform mysteries into observable, measurable diagnostics
              </p>
            </div>
            <button
              onClick={runAllDiagnostics}
              disabled={isRunningAll}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all hover:shadow-lg disabled:opacity-50"
            >
              {isRunningAll ? 'Running All...' : 'Run All Diagnostics'}
            </button>
          </div>

          {/* System Health Overview */}
          {systemHealth.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
              {systemHealth.map(system => (
                <div
                  key={system.name}
                  className={`p-4 rounded-lg border-2 ${
                    system.status === 'healthy'
                      ? 'border-green-300 bg-green-50'
                      : system.status === 'warning'
                      ? 'border-yellow-300 bg-yellow-50'
                      : 'border-red-300 bg-red-50'
                  }`}
                >
                  <h3 className="font-semibold text-sm mb-1">{system.name}</h3>
                  <div className="text-2xl font-bold">
                    {system.passed}/{system.total}
                  </div>
                  <div className="text-xs text-gray-600">
                    {((system.passed / system.total) * 100).toFixed(0)}% passed
                  </div>
                </div>
              ))}
            </div>
          )}
        </header>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white shadow-lg text-blue-600 border-2 border-blue-200'
                    : 'bg-white/70 text-gray-600 hover:bg-white hover:shadow-md'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">
                  🎯 Quick Diagnostics
                </h2>
                <p className="text-gray-600 mb-4">
                  Run comprehensive system checks to identify issues quickly.
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">How it works:</h3>
                    <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Each system is broken down into observable steps</li>
                      <li>Steps are checked sequentially with animations</li>
                      <li>Results show passed ✓, failed ✗, or skipped ○</li>
                      <li>Detailed diagnostics available for each check</li>
                    </ol>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Benefits:</h3>
                    <ul className="text-sm text-green-700 space-y-1 list-disc list-inside">
                      <li>No more mystery bugs - see exactly what fails</li>
                      <li>Share diagnostic reports with developers</li>
                      <li>Track system health over time</li>
                      <li>Identify patterns in failures</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">
                  📈 System Status
                </h2>
                <div className="space-y-4">
                  {tabs.slice(1).map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className="w-full text-left p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl mr-3">{tab.icon}</span>
                          <span className="font-semibold">{tab.label}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          Click to diagnose →
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <ScienceDebugger
              title="⚡ Performance Science"
              description="Measure and optimize application performance"
              steps={performanceSteps}
              autoRun={false}
              onComplete={(results) => handleSystemComplete('Performance', results)}
            />
          )}

          {activeTab === 'network' && (
            <ScienceDebugger
              title="🌐 Network Science"
              description="Diagnose connectivity and API issues"
              steps={networkSteps}
              autoRun={false}
              onComplete={(results) => handleSystemComplete('Network', results)}
            />
          )}

          {activeTab === 'browser' && (
            <ScienceDebugger
              title="🖥️ Browser Science"
              description="Check browser compatibility and capabilities"
              steps={browserSteps}
              autoRun={false}
              onComplete={(results) => handleSystemComplete('Browser', results)}
            />
          )}

          {activeTab === 'errors' && <ErrorScience />}

          {activeTab === 'deployment' && <DeployScience />}

          {activeTab === 'routing' && <RoutingScience />}

          {activeTab === 'storage' && <StorageScience />}
        </div>

        {/* Footer */}
        <footer className="mt-12 p-6 bg-white/70 rounded-xl text-center">
          <p className="text-sm text-gray-600 mb-2">
            💡 <strong>Pro Tip:</strong> Run diagnostics when you encounter issues to get detailed context for debugging.
          </p>
          <p className="text-xs text-gray-500">
            Science Dashboard - Making the invisible visible 🔬
          </p>
        </footer>
      </div>
    </div>
  );
}